/**
 * Electrical Installation Service
 * API integration for electrical installations (Prontuário NR-10), their records and files.
 */
import apiClient from './apiClient';
import {
  ElectricalInstallation,
  ElectricalInstallationCreateData,
  ElectricalInstallationFile,
  ElectricalInstallationFileUploadData,
  ElectricalInstallationFilters,
  ElectricalInstallationFormData,
  ElectricalInstallationListResponse,
  ElectricalRecord,
  ElectricalRecordFormData,
} from '../types/electricalInstallation';

export * from '../types/electricalInstallation';

export interface ElectricalInstallationListParams extends ElectricalInstallationFilters {
  page?: number;
  limit?: number;
}

type Id = string | number;

class ElectricalInstallationService {
  private baseUrl = '/electrical-installations';

  async getInstallations(
    params: ElectricalInstallationListParams = {}
  ): Promise<ElectricalInstallationListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.departmentId !== undefined) queryParams.append('departmentId', String(filters.departmentId));
    if (filters.situation) queryParams.append('situation', filters.situation);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);
    const backendData = response.data.data || response.data;

    return {
      installations: backendData.installations ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  async getInstallationById(id: Id): Promise<ElectricalInstallation> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data || response.data;
  }

  async createInstallation(data: ElectricalInstallationCreateData): Promise<ElectricalInstallation> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data.data || response.data;
  }

  async updateInstallation(
    id: Id,
    data: Partial<ElectricalInstallationFormData>
  ): Promise<ElectricalInstallation> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data.data || response.data;
  }

  async deleteInstallation(id: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  async getRecords(installationId: Id): Promise<ElectricalRecord[]> {
    const response = await apiClient.get(`${this.baseUrl}/${installationId}/records`);
    return response.data.data || response.data;
  }

  async createRecord(installationId: Id, data: ElectricalRecordFormData): Promise<ElectricalRecord> {
    const response = await apiClient.post(`${this.baseUrl}/${installationId}/records`, data);
    return response.data.data || response.data;
  }

  async updateRecord(
    installationId: Id,
    recordId: Id,
    data: Partial<ElectricalRecordFormData>
  ): Promise<ElectricalRecord> {
    const response = await apiClient.put(`${this.baseUrl}/${installationId}/records/${recordId}`, data);
    return response.data.data || response.data;
  }

  async deleteRecord(installationId: Id, recordId: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${installationId}/records/${recordId}`);
  }

  async getFiles(installationId: Id): Promise<ElectricalInstallationFile[]> {
    const response = await apiClient.get(`${this.baseUrl}/${installationId}/files`);
    return response.data.data || response.data;
  }

  async uploadFile(
    installationId: Id,
    file: File,
    data: ElectricalInstallationFileUploadData
  ): Promise<ElectricalInstallationFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentKind', data.documentKind);
    formData.append('title', data.title);
    if (data.issuedAt) formData.append('issuedAt', data.issuedAt);
    if (data.validUntil) formData.append('validUntil', data.validUntil);

    const response = await apiClient.post(`${this.baseUrl}/${installationId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data || response.data;
  }

  async downloadFile(installationId: Id, fileId: Id): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/${installationId}/files/${fileId}/download`, {
      responseType: 'blob',
    });
    return response as unknown as Blob;
  }

  async deleteFile(installationId: Id, fileId: Id): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${installationId}/files/${fileId}`);
  }
}

export const electricalInstallationService = new ElectricalInstallationService();
export default electricalInstallationService;
